import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { apiRequest, formatPrice } from "../api/client";
import Button from "../components/Button";
import CTASection from "../components/CTASection";
import EbookCover from "../components/EbookCover";
import SectionTitle from "../components/SectionTitle";
import { brand, chapters, inside, productHighlights, testimonials } from "../data/siteData";

const samplePages = [
  {
    label: "Page 03",
    title: "Calories made practical",
    text: "Set your starting calories with a simple formula, then learn exactly when to adjust.",
    points: ["Maintenance estimate", "Fat-loss target", "Weekly check-in rules"],
  },
  {
    label: "Page 11",
    title: "Macro setup",
    text: "Turn protein, carbs, and fats into clear daily targets that match your goal.",
    points: ["Protein floor", "Training carbs", "Flexible fats"],
  },
  {
    label: "Page 18",
    title: "Meal building system",
    text: "Create repeatable plates without locking yourself into boring meal plans.",
    points: ["Protein base", "Carb swaps", "Portion control"],
  },
  {
    label: "Page 27",
    title: "Supplements that matter",
    text: "Know what belongs in your routine and what can stay out of your budget.",
    points: ["Creatine", "Whey protein", "Caffeine timing"],
  },
];

const proofStats = [
  { value: "6", label: "Core modules" },
  { value: "30+", label: "Action pages" },
  { value: "100%", label: "Digital access" },
];

export default function EbookDetails() {
  const sampleTrackRef = useRef(null);
  const reviewTrackRef = useRef(null);
  const [product, setProduct] = useState(null);
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    Promise.all([apiRequest("/api/products?featured=true"), apiRequest("/api/testimonials")])
      .then(([productResponse, testimonialResponse]) => {
        setProduct(productResponse.data?.[0] || null);
        setReviews(testimonialResponse.data || []);
      })
      .catch(() => {});
  }, []);

  const scrollTrack = (ref, direction) => {
    const carousel = ref.current;
    if (!carousel) return;

    const cards = Array.from(carousel.querySelectorAll(".sample-page-card, .ebook-review-card"));
    if (!cards.length) return;

    const carouselLeft = carousel.getBoundingClientRect().left;
    const maxScroll = carousel.scrollWidth - carousel.clientWidth;
    const positions = cards.map((card) => Math.min(card.getBoundingClientRect().left - carouselLeft + carousel.scrollLeft, maxScroll));
    const currentIndex = positions.reduce((closestIndex, position, index) => {
      const currentDistance = Math.abs(position - carousel.scrollLeft);
      const closestDistance = Math.abs(positions[closestIndex] - carousel.scrollLeft);
      return currentDistance <= closestDistance ? index : closestIndex;
    }, 0);
    const nextIndex = (currentIndex + direction + cards.length) % cards.length;

    carousel.scrollTo({
      left: positions[nextIndex],
      behavior: "smooth",
    });
  };

  const productName = product?.name || brand.productName;
  const price = product ? formatPrice(product.discount_price || product.price) : brand.price;
  const oldPrice = product?.discount_price ? formatPrice(product.price) : brand.oldPrice;
  const reviewItems = reviews.length
    ? reviews.map((item) => ({
        name: item.customer_name,
        role: item.role || "Verified customer",
        quote: item.review_text,
      }))
    : testimonials;

  return (
    <>
      <section className="detail-hero ebook-detail-hero section">
        <div className="container ebook-detail-grid">
          <motion.div className="ebook-showcase" initial={{ opacity: 0, x: -28 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.55 }}>
            <div className="ebook-cover-wrap">
              <EbookCover />
            </div>
            <div className="ebook-proof-strip">
              {proofStats.map((item) => (
                <div key={item.label}>
                  <strong>{item.value}</strong>
                  <span>{item.label}</span>
                </div>
              ))}
            </div>
          </motion.div>
          <motion.div className="detail-copy ebook-detail-copy" initial={{ opacity: 0, x: 28 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.55 }}>
            <span className="eyebrow">Digital nutrition eBook</span>
            <h1>{productName}</h1>
            <p>
              {product?.description || "A polished, practical system for setting calories, mastering macros, building repeatable meals, and making progress without bouncing between random diets."}
            </p>
            <div className="price-row detail-price">
              <s>{oldPrice}</s>
              <span>{price}</span>
            </div>
            <div className="hero-pills">
              {productHighlights.map((item) => <span key={item}>{item}</span>)}
            </div>
            <div className="ebook-actions">
              <Button to={product ? `/products/${product.slug}` : "/checkout"}>Buy Now</Button>
              <Button to="/contact" variant="secondary">Ask a Question</Button>
            </div>
            <div className="ebook-includes">
              <span>Instant PDF access</span>
              <span>Beginner friendly structure</span>
              <span>Built for fat loss, lean gaining, and maintenance</span>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="section section-dark">
        <div className="container">
          <SectionTitle title="What you will" highlight="learn" />
          <div className="grid-4">
            {inside.map((item) => (
              <article className="glass-card mini-card" key={item.title}>
                <span className="card-icon">{item.icon}</span>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container chapter-layout">
          <SectionTitle align="left" title="Included" highlight="chapters" text="Structured modules that move from fundamentals to execution." />
          <div className="chapter-list">
            {chapters.map((chapter, index) => (
              <motion.div
                className="chapter-row"
                key={chapter}
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
              >
                <span>{String(index + 1).padStart(2, "0")}</span>
                <p>{chapter}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="section ebook-samples-section">
        <div className="container">
          <div className="ebook-section-head">
            <SectionTitle align="left" title="Preview sample" highlight="pages" text="A quick look at the kind of frameworks, checklists, and execution pages inside the guide." />
            <div className="carousel-controls">
              <button type="button" aria-label="Previous sample page" onClick={() => scrollTrack(sampleTrackRef, -1)}>‹</button>
              <button type="button" aria-label="Next sample page" onClick={() => scrollTrack(sampleTrackRef, 1)}>›</button>
            </div>
          </div>
          <div className="sample-carousel" ref={sampleTrackRef}>
            <div className="sample-track">
              {samplePages.map((page, index) => (
                <motion.article
                  className="sample-page-card"
                  key={page.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.06 }}
                >
                  <div className="sample-page-paper">
                    <div className="sample-page-top">
                      <span>{page.label}</span>
                      <strong>{String(index + 1).padStart(2, "0")}</strong>
                    </div>
                    <h3>{page.title}</h3>
                    <p>{page.text}</p>
                    <ul>
                      {page.points.map((point) => <li key={point}>{point}</li>)}
                    </ul>
                    <div className="sample-page-footer">
                      <span>{productName}</span>
                      <b>{page.label.replace("Page ", "")}</b>
                    </div>
                  </div>
                </motion.article>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="section section-dark ebook-reviews-section">
        <div className="container">
          <div className="ebook-section-head">
            <SectionTitle align="left" title="Reviews &" highlight="results" text="Real feedback from clients who used the system to make nutrition feel measurable and repeatable." />
            <div className="carousel-controls">
              <button type="button" aria-label="Previous review" onClick={() => scrollTrack(reviewTrackRef, -1)}>‹</button>
              <button type="button" aria-label="Next review" onClick={() => scrollTrack(reviewTrackRef, 1)}>›</button>
            </div>
          </div>
          <div className="review-carousel" ref={reviewTrackRef}>
            <div className="review-track">
            {reviewItems.map((item) => (
              <article className="glass-card testimonial-card ebook-review-card" key={item.name}>
                <div className="stars">★★★★★</div>
                <p>"{item.quote}"</p>
                <strong>{item.name}</strong>
                <span>{item.role}</span>
              </article>
            ))}
            </div>
          </div>
        </div>
      </section>

      <CTASection />
    </>
  );
}
